import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Hash,
  Loader2,
  ListChecks,
  Plus,
  Users,
  FileText,
  Building2,
  CheckCircle2,
  Pencil,
} from "lucide-react";
import Pill from "@/components/Pill";
import AddInlineDialog from "@/components/AddInlineDialog";
import ProjectStatusDialog from "@/components/ProjectStatusDialog";
import { useRefData } from "@/context/RefDataContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";

export default function BizProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();
  const [doc, setDoc] = useState(null);
  const [related, setRelated] = useState({ tasks: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // "task" | null
  const [statusOpen, setStatusOpen] = useState(false);

  const refreshRelated = useCallback(async () => {
    try {
      const { data } = await api.get(`/biz-projects/${id}/related`);
      setRelated(data);
    } catch (_) {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/biz-projects/${id}`),
          api.get(`/biz-projects/${id}/related`),
        ]);
        setDoc(a.data);
        setRelated(b.data);
      } catch (e) {
        toast.error("Project not found");
        navigate("/biz-projects");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="biz-project-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading project…
      </div>
    );
  }
  if (!doc) return null;

  const contacts = Array.isArray(doc.contact_persons) ? doc.contact_persons : [];
  const files = Array.isArray(doc.files) ? doc.files : [];

  const taskCfg = {
    endpoint: "/tasks",
    title: "New Task",
    fields: [
      { key: "subject", label: "Subject", required: true, full: true },
      { key: "biz_project_id", label: "Project", type: "select", optionsRef: "biz_projects" },
      { key: "team_id", label: "Team", type: "select", optionsRef: "teams" },
      { key: "priority", label: "Priority", type: "select", optionsRef: "priorities" },
      { key: "status", label: "Status", type: "select", optionsRef: "statuses" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "details", label: "Details", type: "textarea", full: true },
    ],
    initialForm: {
      subject: "",
      biz_project_id: id,
      team_id: doc.team_id || "",
      priority: "Medium",
      status: "Open",
      due_date: "",
      details: "",
    },
    locked: ["biz_project_id"],
  };

  return (
    <div className="space-y-8" data-testid="biz-project-detail-page">
      <div>
        <Link
          to="/biz-projects"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="biz-project-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Projects
        </Link>
      </div>

      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            <span className="font-mono normal-case tracking-normal" data-testid="biz-project-detail-code">
              {doc.code}
            </span>
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "Outfit" }}
            data-testid="biz-project-detail-name"
          >
            {doc.project_name || doc.customer_name}
          </h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="biz-project-status-row">
            <ProjectStatusPill status={doc.project_status || "New Project"} />
            {can(user, "biz_projects", "update") && (
              <button
                type="button"
                onClick={() => setStatusOpen(true)}
                className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
                data-testid="biz-project-status-edit"
              >
                <Pencil className="w-3 h-3" /> Change
              </button>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-2 flex items-center gap-x-3 gap-y-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="w-4 h-4" /> {doc.customer_name || "—"}
            </span>
            <span className="text-zinc-500 font-mono text-xs">
              PO: {doc.po_number || "—"}
            </span>
            <span className="text-zinc-500 font-mono text-xs">
              Proposal: {doc.proposal_number || "—"}
            </span>
            <span className="text-zinc-500 font-mono text-xs">
              Rel #: {doc.relationship_number || "—"}
            </span>
            <span className="text-zinc-500 text-xs">
              Created: {doc.start_date || "—"}
            </span>
            <span className="text-zinc-500 text-xs">
              Due: {doc.due_date || "—"}
            </span>
            {doc.map_link ? (
              <a
                href={doc.map_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline"
              >
                <MapPin className="w-3.5 h-3.5" /> Map <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
                <MapPin className="w-3.5 h-3.5" /> Map: —
              </span>
            )}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/biz-projects")}
          className="bg-zinc-900 border border-white/10 text-white hover:bg-zinc-800"
        >
          Back to list
        </Button>
      </header>

      {doc.description && (
        <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
          <div className="label-overline mb-2">Notes</div>
          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{doc.description}</p>
        </section>
      )}

      {doc.project_status === "Completed" && (
        <section
          className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-5"
          data-testid="biz-project-completion"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <div className="label-overline" style={{ color: "#34d399" }}>
              Project Completed
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">
                Completion Date &amp; Time
              </div>
              <div className="text-white">{doc.completion_date_time || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">
                Report Number
              </div>
              <div className="text-white font-mono">{doc.completion_report_number || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">
                Report PDF
              </div>
              {doc.completion_report_file ? (
                <a
                  href={`${api.defaults.baseURL}/files/${doc.completion_report_file.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-sky-400 hover:underline"
                  data-testid="biz-project-completion-file"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[200px]">
                    {doc.completion_report_file.filename}
                  </span>
                </a>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Linked Tasks */}
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid="biz-related-tasks">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 label-overline">
            <ListChecks className="w-3.5 h-3.5" strokeWidth={1.75} /> Linked tasks
          </div>
          <div className="flex items-center gap-3">
            <Link to="/tasks" className="text-xs text-zinc-500 hover:text-white">
              View all →
            </Link>
            {can(user, "tasks", "create") && (
              <button
                type="button"
                onClick={() => setAdding("task")}
                className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
                data-testid="biz-add-task-btn"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            )}
          </div>
        </div>
        {!related.tasks || related.tasks.length === 0 ? (
          <div className="text-xs text-zinc-600 py-6 text-center">Nothing linked yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {related.tasks.slice(0, 20).map((t) => (
              <li
                key={t.id}
                className="py-2.5 flex items-center justify-between gap-3"
                data-testid={`biz-related-task-${t.id}`}
              >
                <div className="min-w-0">
                  <Link to={`/tasks/${t.id}`} className="text-sm text-white truncate hover:text-sky-300">
                    {t.subject}
                  </Link>
                  <div className="text-xs text-zinc-500 font-mono">{t.code}</div>
                </div>
                <div className="flex gap-1.5 items-center shrink-0">
                  <Pill kind="priority" value={t.priority} />
                  <Pill kind="status" value={t.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Contact persons */}
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 label-overline">
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} /> Contact persons
          </div>
        </div>
        {contacts.length === 0 ? (
          <div className="text-xs text-zinc-600 py-6 text-center">No contact persons.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map((c, idx) => (
              <div key={idx} className="bg-zinc-950 border border-white/10 rounded-lg p-4">
                <div className="text-sm font-medium text-white">{c.name || "Unnamed"}</div>
                <div className="mt-3 space-y-1.5">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
                      <Mail className="w-3.5 h-3.5" />
                      <span className="truncate">{c.email}</span>
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
                      <Phone className="w-3.5 h-3.5" />
                      <span>{c.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Files */}
      {files.length > 0 && (
        <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
          <div className="label-overline mb-3">Attachments</div>
          <div className="flex flex-wrap gap-2">
            {files.map((f) => (
              <a
                key={f.id}
                href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline bg-zinc-950 border border-white/10 rounded-md px-2 py-1"
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="truncate max-w-[180px]">{f.filename}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      <footer className="text-xs text-zinc-600 pt-4 border-t border-white/5">
        Created {doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}
      </footer>

      <AddInlineDialog
        open={adding === "task"}
        onOpenChange={(o) => !o && setAdding(null)}
        endpoint={taskCfg.endpoint}
        title={taskCfg.title}
        fields={taskCfg.fields}
        initialForm={taskCfg.initialForm}
        lockedKeys={taskCfg.locked}
        onCreated={() => {
          setAdding(null);
          refreshRelated();
          ref.refresh?.();
        }}
      />

      <ProjectStatusDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        projectId={id}
        currentStatus={doc.project_status || "New Project"}
        onUpdated={(updated) => {
          setStatusOpen(false);
          setDoc(updated);
        }}
      />
    </div>
  );
}

const STATUS_STYLES = {
  "New Project": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  Planning: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

function ProjectStatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-white/5 text-zinc-300 border-white/10";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`}
      data-testid="biz-project-status-pill"
    >
      <span className="w-1 h-1 rounded-full bg-current opacity-80" />
      {status}
    </span>
  );
}
